import PropTypes from 'prop-types'
import { ResponsiveBar } from '@nivo/bar'
import { useTheme } from '@emotion/react'
import { Section, Tooltip } from 'chaoskit/src/components'

import ResponsiveGraphWrapper from './ResponsiveGraphWrapper'
import commonProperties from './common.json'
import { getBarVariants, getChartColors, styles } from './utilities'
import { ContentGrid } from './Content'
import GraphLegend from './GraphLegend'

import Data from '~components/Data'

function getCustomColorsByIndex(dataset, colorsByIndex) {
  if (!colorsByIndex) return false

  return Object.keys(dataset[0]).slice(1).length
}

export default function BarGraph({
  keys,
  dataset,
  graphProps = {},
  colorsByIndex,
  ...rest
}) {
  return (
    <ResponsiveGraphWrapper {...rest}>
      <ResponsiveBar
        {...commonProperties}
        colors={getChartColors(
          getCustomColorsByIndex(dataset, colorsByIndex) || dataset.length
        )}
        data={dataset}
        keys={keys}
        indexBy="id"
        labelTextColor={{
          from: 'color',
          modifiers: [['brighter', 6]],
        }}
        labelSkipWidth={16}
        labelSkipHeight={16}
        {...graphProps}
      />
    </ResponsiveGraphWrapper>
  )
}

BarGraph.propTypes = {
  keys: PropTypes.array.isRequired,
  dataset: PropTypes.array.isRequired,
  graphProps: PropTypes.object,
  colorsByIndex: PropTypes.bool,
}

export function BarSection({
  dataset,
  graphProps = {},
  title,
  type = '',
  graphOverrides = {},
  colorsByIndex,
  altLegend,
}) {
  const theme = useTheme()

  // Assuming `id` is the first property; which we don't need
  const keys = type.includes('grouped')
    ? Object.keys(dataset[0]).slice(1)
    : ['value']

  const typeProps = getBarVariants(type, graphOverrides)

  return (
    <Section space="base">
      <ContentGrid
        css={{
          alignItems: 'center',
        }}
      >
        <div
          css={{
            fontSize: theme.fontSize.large,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1, // So it sits above overlapping graph

            [theme.mq.medium]: {
              fontSize: theme.fontSize.xlarge,
              textAlign: 'right',
            },
          }}
        >
          <Data>{title}</Data>
        </div>
        <div>
          <BarGraph
            keys={keys}
            dataset={dataset}
            graphProps={{ ...typeProps, ...graphProps }}
            colorsByIndex={colorsByIndex}
          />
          <GraphLegend
            dataset={dataset}
            colorsByIndex={getCustomColorsByIndex(dataset, colorsByIndex)}
            altLegend={altLegend}
            css={{
              marginTop: theme.space.base,
            }}
          />
        </div>
      </ContentGrid>
    </Section>
  )
}

BarSection.propTypes = {
  dataset: PropTypes.array.isRequired,
  graphProps: PropTypes.object,
  title: PropTypes.string.isRequired,
  colorsByIndex: PropTypes.bool,
  type: PropTypes.oneOf([
    'vertical',
    'vertical-grouped',
    'vertical-grouped-stacked',
    'horizontal-grouped',
    'horizontal-grouped-stacked',
  ]),
  graphOverrides: PropTypes.shape({
    noPercentageAxis: PropTypes.bool,
    noPercentageLabels: PropTypes.bool,
    noAxisLeft: PropTypes.bool,
  }),
  altLegend: PropTypes.bool,
}

export function StackedBarSection({ graphs }) {
  return graphs.map((graph) => (
    <BarSection
      key={graph.heading}
      dataset={graph.data}
      title={graph.heading}
      type={graph.type}
    />
  ))
}

StackedBarSection.propTypes = {
  graphs: PropTypes.array.isRequired,
}

const StaticLayoutBarGraphChunk = ({ value, color, legendLabel, isLast }) => {
  const theme = useTheme()

  return (
    <Tooltip
      enableFollowCursor
      variation="dark"
      content={
        <div
          css={{
            fontFamily: styles.font.mono,
            fontSize: theme.fontSize.xsmall,
          }}
        >
          {legendLabel}: {value}%
        </div>
      }
    >
      <div
        css={[
          {
            textAlign: 'center',
            fontSize: theme.fontSize.xsmall,
            padding: theme.space.small,
            position: 'relative',

            '&:not(:first-of-type)': {
              marginLeft: theme.space.xsmall,
            },
          },

          !isLast && {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          },
        ]}
        style={{ background: color, width: `${value}%` }}
      >
        <span
          css={[
            isLast && {
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translate(100%, -50%)',
              display: 'flex',
            },
          ]}
        >
          {isLast && (
            <span css={{ color, marginRight: theme.space.small }}>&mdash;</span>
          )}
          {value}%
        </span>
      </div>
    </Tooltip>
  )
}

StaticLayoutBarGraphChunk.propTypes = {
  color: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  isLast: PropTypes.bool,
  legendLabel: PropTypes.string.isRequired,
}

// Uses normal HTML/CSS to render parts for advanced axis/layout requirements
export const StaticLayoutBarGraph = ({ graph }) => {
  const theme = useTheme()

  const colorMap = getChartColors(graph.graph[0].data.length)

  return (
    <Section space="base">
      <ContentGrid>
        <div
          css={{
            fontSize: theme.fontSize.large,
            textAlign: 'center',

            [theme.mq.medium]: {
              fontSize: theme.fontSize.xlarge,
              textAlign: 'left',
            },
          }}
        >
          <Data>{graph.heading}</Data>
        </div>
        <div>
          <div
            css={{
              position: 'relative',
              fontFamily: styles.font.mono,
              marginRight: theme.space.xlarge, // Helps space-out floating final values
            }}
          >
            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
              }}
            >
              {[...Array(14)].map((_, index) => {
                const key = `graph-${index}`

                return (
                  <div
                    key={key}
                    css={{
                      background: commonProperties.theme.grid.line.stroke,
                      opacity:
                        index === 0 || index === 13
                          ? commonProperties.theme.grid.line.opacity
                          : commonProperties.theme.grid.line.opacity / 2,
                      width: 1,
                      height: '100%',
                    }}
                  />
                )
              })}
            </div>
            {graph.graph.map((chunk) => (
              <div
                key={chunk.heading}
                css={{
                  position: 'relative',
                  zIndex: 1,
                  marginTop: theme.space.small,
                  marginBottom: theme.space.small,
                }}
              >
                <div
                  key={chunk.heading}
                  css={{
                    display: 'flex',
                  }}
                >
                  {chunk.data.map((point, index) => (
                    <StaticLayoutBarGraphChunk
                      key={point.id}
                      legendLabel={point.id}
                      value={point.value}
                      color={colorMap[index]}
                      isLast={index === chunk.data.length - 1}
                    />
                  ))}
                </div>
                <div
                  css={{
                    fontSize: theme.fontSize.xsmall,
                    paddingLeft: theme.space.small,
                    paddingRight: theme.space.small,
                  }}
                >
                  {chunk.heading}
                </div>
              </div>
            ))}
          </div>
          <GraphLegend
            dataset={graph.graph[0].data}
            css={{
              marginTop: theme.space.large,
            }}
          />
        </div>
      </ContentGrid>
    </Section>
  )
}

StaticLayoutBarGraph.propTypes = {
  graph: PropTypes.object.isRequired,
}
